// Redux services
import { createOrder as createOrderRedux } from 'redux/services/orders'

// Local
import { RootState, TypedDispatch } from 'redux/store'
import { FinalizeOrderArgs } from '../types'
import { SanitizedMember } from 'models/Member'
import { MethodDetails, Order, OrdersPaymentSource } from 'models/Orders'
import { PaymentMethod, PaymentProcessor } from 'models/ChargeIntent'
import {
  ACHFinalizationData,
  StripeFinalizationData,
  TokenExFinalizationData,
} from 'models/PaymentDetails'

import getStripePromise from 'utils/functions/getStripePromise'
import handleStripeError from 'utils/functions/handleStripeError'

import buildOrdersDedicationObject from './buildDedicationObject'
import { SantizedSupporter } from 'models/Supporter'
import cartsApi from 'redux/services/carts'
import { CartAttributes, UpdateCartRequest } from 'models/Carts'
import { StripeError } from '@stripe/stripe-js'

/**
 * TBD
 *
 * @param store
 * @param dispatch
 * @returns Order
 */
const createOrder = async (
  store: RootState,
  dispatch: TypedDispatch<RootState>,
  args: FinalizeOrderArgs,
  member: SanitizedMember,
  supporter: SantizedSupporter,
): Promise<Order> => {
  const answers = Object.values(store.order.answers).map((answer) => {
    return { questionId: Number(answer.questionId), value: answer.answer.toString() }
  })

  enum OrdersPaymentMethod {
    CC = 'creditCard',
    ACH = 'ach',
    Plaid = 'stripe',
    PayPal = 'paypal',
    PayPal_Commerce = 'paypal',
    Venmo = 'venmo',
    Stripe = 'stripe',
    DW = 'stripe',
  }

  const isStripeCC =
    store.order.paymentDetails.type === PaymentMethod.CC &&
    store.order.paymentDetails.processor === PaymentProcessor.STRIPE

  const isRecurring = args.frequency && args.frequency !== 'one-time'

  /*
   * /processor/latest returns STRIPE processors for both CC and STRIPE
   * to simplify on the frontend we treat Stripe paymentmethod as CC
   * so we have to do this to get the correct method that Orders API is expecting
   */
  const orderMethod = isStripeCC
    ? OrdersPaymentMethod.Stripe
    : OrdersPaymentMethod[store.order.paymentDetails.type]

  const paymentDetails = store.order.paymentDetails
  let details: MethodDetails
  let paymentSource: OrdersPaymentSource = OrdersPaymentSource.MANUAL

  switch (paymentDetails.type) {
    case PaymentMethod.CC:
    case PaymentMethod.Stripe:
    case PaymentMethod.DW:
      if (store.order.paymentDetails.processor === PaymentProcessor.STRIPE) {
        details = {
          source: (store.order.paymentDetails.method as StripeFinalizationData).id as string,
          isPaymentIntent: true,
        }
      } else {
        details = store.order.paymentDetails.method as TokenExFinalizationData
      }

      if (paymentDetails.type === PaymentMethod.DW) {
        const walletType = store.order.pmStatus.digitalWallets.walletType
        paymentSource = walletType ? OrdersPaymentSource[walletType] : OrdersPaymentSource.WALLET
      }

      break
    case PaymentMethod.ACH:
      details = {
        accountNumber: (store.order.paymentDetails.method as ACHFinalizationData).accountNumber,
        routingNumber: (store.order.paymentDetails.method as ACHFinalizationData).routingNumber,
      }

      break
    case PaymentMethod.Plaid:
      if (orderMethod === OrdersPaymentMethod.Stripe) {
        details = {
          source: (store.order.paymentDetails.method as { publicToken: string }).publicToken,
          accountId: (store.order.paymentDetails.method as { accountId: string }).accountId,
        }
      } else {
        details = {
          publicToken: (store.order.paymentDetails.method as { publicToken: string }).publicToken,
          accountId: (store.order.paymentDetails.method as { accountId: string }).accountId,
        }
      }

      break
    case PaymentMethod.PayPal:
      details = {
        nonce: (store.order.paymentDetails.method as { nonce: string }).nonce,
      }

      break
    case PaymentMethod.PayPal_Commerce:
      if (isRecurring) {
        details = {
          agreementId: (store.order.paymentDetails.method as { agreementId: string }).agreementId,
        }
      } else {
        details = {
          paypalOrderId: (store.order.paymentDetails.method as { orderId: string }).orderId,
        }
      }

      paymentSource = OrdersPaymentSource.PAYPAL

      break
    case PaymentMethod.Venmo:
      details = {
        venmoOrderId: (store.order.paymentDetails.method as { orderId: string }).orderId,
      }

      paymentSource = OrdersPaymentSource.VENMO

      break
    default:
      throw new Error('not supported')
  }

  // used to only include non-falsey values in our request bodies
  const wrap = (key: string, value?: unknown) => (value ? { [key]: value } : {})

  const billingAddress = store.order.billingDetails.address

  const billingInfo = {
    firstName: store.order.billingDetails.firstName,
    lastName: store.order.billingDetails.lastName,
    email: store.order.billingDetails.email,
    ...wrap('address1', billingAddress.line1),
    ...wrap('address2', billingAddress.line2),
    ...wrap('city', billingAddress.city),
    ...wrap('state', billingAddress.state),
    ...wrap('country', billingAddress.country),
    ...wrap('postalCode', billingAddress.postalCode),
  } as CartAttributes['billingInfo']

  const paymentInfo = {
    type: 'online',
    method: orderMethod,
    methodDetails: details,
    source: paymentSource,
    // statementDescriptor: 'Donation to xyz',
  } as CartAttributes['paymentInfo']

  const dedication = store.order.dedication.hasDedication
    ? buildOrdersDedicationObject(store.order.dedication)
    : null

  // it's possible for company name to be in the store with isCompanyCard==false if the donor
  // filled out the field, then unchecked the box
  const companyName = store.order.billingDetails.isCompanyCard
    ? store.order.billingDetails.companyName
    : null

  const cartId = args.cart_id as string

  const updateCartBody: UpdateCartRequest = {
    data: {
      type: 'carts' as const,
      attributes: {
        billingInfo,
        paymentInfo,
        donorCoversFees: store.order.paymentDetails.dcf,
        isAmountHidden: store.order.paymentDetails.isOrderPrivate,
        isAnonymous: store.order.paymentDetails.isOrderAnonymous,
        isGiftAid: false,
        comment: store.order.donationOptions.comment,
        ...wrap('sourceCode1', args.c_src),
        ...wrap('sourceCode2', args.c_src2),
        ...wrap('sourceCodeReferrer', args.referrer),
        ...wrap('companyName', companyName),
        supporterId: Number(supporter.id),
        memberId: Number(member.id),
        ...wrap('dedication', dedication),
        answers,
      },
    },
  }

  // update cart
  try {
    await dispatch(
      cartsApi.endpoints.updateCart.initiate({ cartId, body: updateCartBody }),
    ).unwrap()
  } catch (e) {
    throw new Error(`Could not update cart ${cartId}`, { cause: e })
  }

  // submit order
  let order

  try {
    order = await dispatch(
      createOrderRedux.initiate({
        campaignId: args.cid,
        paymentMethod: isStripeCC ? PaymentMethod.Stripe : store.order.paymentDetails.type,
        body: {
          data: {
            type: 'orders',
            attributes: {
              cartId,
            },
            reCaptchaToken: args.reCaptchaToken,
          },
        },
      }),
    ).unwrap()
  } catch (e) {
    const error = (e as { data: { errors: StripeError[] } })?.data?.errors?.[0]

    if (error) {
      const errorMessage = handleStripeError(error as StripeError)
      throw new Error(errorMessage, { cause: error })
    }

    throw e
  }

  if (
    paymentDetails.processor === PaymentProcessor.STRIPE &&
    paymentDetails.type !== PaymentMethod.ACH &&
    paymentDetails.type !== PaymentMethod.Plaid
  ) {
    const stripe = await getStripePromise(dispatch)
    // Sanity check on stripe
    if (!stripe) throw new Error('Could not finalize payment. Unable to initialize Stripe.')

    const response = await stripe.confirmPayment({
      clientSecret: order.data.attributes.paymentIntent.clientSecret,
      redirect: 'if_required',
    })

    const { error } = response

    if (error) {
      const errorMessage = handleStripeError(error)
      throw new Error(errorMessage)
    }
  }

  return order.data
}

export default createOrder
